// Pure selectors that turn query data into Book module face props. No React,
// no DOM. Every function tolerates undefined, empty and malformed input: what
// cannot be known is null (never 0), and no result ever carries a NaN or an
// Infinity. The Book faces (BookModulesRow1-3) render exactly what these return.
import type { positionsApi, taxApi } from '../../api/client'
import {
  addBusinessDays,
  addCalendarDays,
  daysUntil,
  etTodayISO,
  fmtISO,
  nextDiscoveryISO,
  nextTuesdayISO,
  parseISO,
  shortDate,
} from '../../lib/cadence'
import { THETA_MAX, THETA_MIN } from '../../lib/theta'
import type { Timeframe } from '../../lib/timeframe'
import { heatScale } from '../../lib/heatScale'
import type {
  Book,
  ConfidenceLog,
  ConfidenceLogRow,
  ConvictionRoster,
  ConvictionScorecard,
  ConvictionStatus,
  DrawdownFloorStatus,
  OverlayView,
  RosterCandidate,
  ScorecardPosition,
  SectorShiftRunDay,
  SectorShiftRuns,
} from '../../types'
import type { BulletFlag, BulletRow } from '../viz/BulletBars'
import type { HeatCell } from '../viz/HeatGrid'
import type { HistBin } from '../viz/Histogram'
import type { StripPoint } from '../viz/StripPlot'
import type { TimelineEvent, TimelineLane } from '../viz/Timeline'
import { fmtPct, num } from './fleetSummaries'

// The two api shapes the selectors read. They are derived from the client, not
// taken from types/index.ts, whose Position is another shape.
export type TaxSummary = Awaited<ReturnType<typeof taxApi.getSummary>>
export type Position = Awaited<ReturnType<typeof positionsApi.getAll>>[number]

const DASH = '—'

/** The live-slot limit (fleetModuleHooks POSITION_CAP, BenchPanel LIVE_CAP). */
const POSITION_CAP = 25

// ------------------------------------------------------------------ helpers

/** The object entries of `v` when it is an array; anything else is empty. */
const objects = <T extends object>(v: unknown): T[] =>
  Array.isArray(v) ? v.filter((x): x is T => x !== null && typeof x === 'object') : []

const isRecord = (v: unknown): v is Record<string, unknown> =>
  v !== null && typeof v === 'object' && !Array.isArray(v)

/** `n`, or null when it is not a finite number (an overflowed sum, say). */
const finite = (n: number): number | null => (Number.isFinite(n) ? n : null)

const compareText = (a: string, b: string): number => (a < b ? -1 : a > b ? 1 : 0)

const nonEmptyText = (v: unknown): string | null => (typeof v === 'string' && v.trim() !== '' ? v : null)

/** A real calendar date in YYYY-MM-DD form ('2026-02-31' is not one). */
function isISODate(v: unknown): v is string {
  if (typeof v !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(v)) return false
  const d = parseISO(v)
  return !Number.isNaN(d.getTime()) && fmtISO(d) === v
}

/** The calendar day of a YYYY-MM-DD date or an ISO timestamp, or null when it is neither. */
function dayOf(v: unknown): string | null {
  const day = typeof v === 'string' ? v.slice(0, 10) : null
  return isISODate(day) ? day : null
}

/** Ends in a zone designator: Z, +HH:MM, +HHMM or +HH. */
const HAS_ZONE = /(Z|[+-]\d{2}(:?\d{2})?)$/i

/**
 * The ET calendar day of an API timestamp, the day `today` is in (etTodayISO). A
 * run that finishes after 8pm ET is already the next day in UTC, so slicing the
 * first ten characters would put it on the wrong day. A plain YYYY-MM-DD is
 * already a calendar day and is kept as it is. A timestamp with no zone is read as
 * UTC, the backend's own rule, not as the viewer's local time. Null when it is not
 * a real date followed by a readable time.
 */
function etDayOf(v: unknown): string | null {
  if (typeof v !== 'string' || dayOf(v) === null) return null
  if (v.length === 10) return v
  const at = new Date(HAS_ZONE.test(v) ? v : `${v}Z`)
  return Number.isNaN(at.getTime()) ? null : etTodayISO(at)
}

// ----------------------------------------------------------- mirrorSummary

export interface MirrorSummary {
  state: 'no-source' | 'awaiting' | 'failed' | 'synced' | 'drift'
  synced: number
  pendingEntry: number
  pendingExit: number
  asOf: string | null
  error: string | null
}

/** String entries of a tick list, or null when the tick has no such array (MirrorFidelityPanel's strList). */
const tickCount = (v: unknown): number | null =>
  Array.isArray(v) ? v.filter((x) => typeof x === 'string').length : null

/**
 * The MirrorFidelityPanel ladder for one subscriber book, from the engine's
 * in-memory subscriber tick report. No pick source → no-source. Then a stale or
 * errored tick → failed, decided BEFORE awaiting so that a failure is never
 * hidden behind "no arrays yet". Then no tick, or a tick with none of
 * holds/entries/exits → awaiting. Otherwise drift when anything is pending, else
 * synced. Counts are the string entries of those three arrays.
 */
export function mirrorSummary(book: Book | undefined): MirrorSummary {
  const none = { synced: 0, pendingEntry: 0, pendingExit: 0, asOf: null, error: null }
  if (!book || book.pick_source_account_id == null) return { state: 'no-source', ...none }

  const raw = book.bookStatus?.last_conviction_tick
  const tick = isRecord(raw) ? raw : null
  if (tick === null) return { state: 'awaiting', ...none }

  const holds = tickCount(tick.holds)
  const entries = tickCount(tick.entries)
  const exits = tickCount(tick.exits)
  const counts = { synced: holds ?? 0, pendingEntry: entries ?? 0, pendingExit: exits ?? 0 }
  const asOf = typeof tick.as_of === 'string' ? tick.as_of : null

  if (tick.stale === true || 'error' in tick) {
    return { state: 'failed', ...counts, asOf, error: typeof tick.error === 'string' ? tick.error : null }
  }
  if (holds === null && entries === null && exits === null) {
    return { state: 'awaiting', ...counts, asOf, error: null }
  }
  const drift = counts.pendingEntry + counts.pendingExit > 0
  return { state: drift ? 'drift' : 'synced', ...counts, asOf, error: null }
}

// ---------------------------------------------------------------- liveGate

export interface LiveGate {
  state: 'dormant' | 'halted' | 'running'
  reason: string | null
  gates: { registry: boolean; feed: boolean; engine: boolean }
}

/**
 * DormancyPanel's precedence: a book that is not in the engine is dormant; one
 * that is built but halted is halted (with the halt reason, null when none was
 * reported); else running. The three gates are the double lock a live book
 * needs: an enabled registry row, a feed subscription, an engine book.
 */
export function liveGate(book: Book | undefined): LiveGate {
  const status = book?.bookStatus ?? null
  const gates = {
    registry: book?.trading_enabled === true,
    feed: book?.pick_source_account_id != null,
    engine: status !== null,
  }
  if (status === null) return { state: 'dormant', reason: null, gates }
  if (status.halted === true) return { state: 'halted', reason: nonEmptyText(status.halt_reason), gates }
  return { state: 'running', reason: null, gates }
}

// ------------------------------------------------------------ valveSummary

export interface ValveSummary {
  theta: number | null
  top: { symbol: string; score: number } | null
  names: number
  held: number | null
  logDate: string | null
  exits30d: number | null
}

/**
 * The status θ, or null: a missing, non-finite or non-positive θ is unknown, never a
 * default. The backend reports `ThesisCfg().theta` (0.8) whenever its conviction
 * service is not registered (a non-leader task, a failover, a stopped engine) and says
 * so with `service_running: false`, so θ is a fact only from a running service.
 */
const thetaOf = (status: ConvictionStatus | undefined): number | null => {
  if (status?.service_running !== true) return null
  const t = num(status.theta)
  return t !== null && t > 0 ? t : null
}

interface ScoredRow {
  symbol: string
  score: number
}

/**
 * The latest log day and the rows on it that carry a usable score. The day is
 * the latest over every dated row, so a latest day whose scores are all unusable
 * reads as "no scores", not as the day before passed off as current. A truncated
 * log has no known latest day (its newest days were dropped), so it reads the same.
 */
function latestScores(log: ConfidenceLog | undefined): { logDate: string | null; rows: ScoredRow[] } {
  // A truncated log dropped its NEWEST days (rows ascending, capped), so its last day is an old one.
  if (log?.truncated === true) return { logDate: null, rows: [] }
  const dated = objects<ConfidenceLogRow>(log?.rows).flatMap((r) => {
    const day = dayOf(r.log_date)
    return day === null ? [] : [{ day, symbol: typeof r.symbol === 'string' ? r.symbol : '', score: num(r.score) }]
  })
  if (dated.length === 0) return { logDate: null, rows: [] }
  const logDate = dated.reduce((m, r) => (r.day > m ? r.day : m), dated[0].day)
  const rows = dated.flatMap((r) =>
    r.day === logDate && r.score !== null ? [{ symbol: r.symbol, score: r.score }] : [])
  return { logDate, rows }
}

/**
 * The exit valve at a glance (ruling 24: the valve fires when a name's
 * thesis-break score is >= θ, so the name to watch is the one with the HIGHEST
 * score). `top` is the highest score among the rows of the latest log day, the
 * symbol breaking a tie; `names` is how many rows that day scored. `held` and
 * `exits30d` come from the scorecard (null without one): the held count, and the
 * thesis-break exits dated within 30 days of `today`.
 */
export function valveSummary(
  status: ConvictionStatus | undefined,
  scorecard: ConvictionScorecard | undefined,
  log: ConfidenceLog | undefined,
  today: string,
): ValveSummary {
  const { logDate, rows } = latestScores(log)
  let top: ScoredRow | null = null
  for (const r of rows) {
    if (top === null || r.score > top.score || (r.score === top.score && r.symbol < top.symbol)) top = r
  }

  const positions = Array.isArray(scorecard?.positions) ? objects<ScorecardPosition>(scorecard?.positions) : null
  const cutoff = isISODate(today) ? addCalendarDays(today, -30) : null

  return {
    theta: thetaOf(status),
    top: top === null ? null : { symbol: top.symbol, score: top.score },
    names: rows.length,
    held: positions === null ? null : positions.filter((p) => p.state === 'held').length,
    logDate,
    exits30d:
      positions === null || cutoff === null
        ? null
        : positions.filter((p) => {
            const day = dayOf(p.exited_date)
            return p.reason === 'thesis_break' && day !== null && day >= cutoff
          }).length,
  }
}

// ---------------------------------------------------- stale logs, score caveats

/**
 * A confidence log whose latest day is more than this many calendar days before ET
 * today is stale (a weekend plus a holiday is 4): its scores are real, only old, and
 * must say so. The one bar every Book face applies, so two tiles on a page can never
 * disagree about whether the scores are current.
 */
export const STALE_LOG_DAYS = 4

/**
 * Whether the latest log day is too old to be called current. `daysUntil(target,
 * today)` is target - today, so the age of the log is today - logDate. A missing,
 * future or unreadable date is not stale (NaN compares false): nothing is known to be
 * old. A plain boolean, not a type guard: a guard would narrow a fresh date to `null`
 * in its false branch.
 */
export function isStaleLog(logDate: string | null, today: string): boolean {
  return logDate !== null && daysUntil(today, logDate) > STALE_LOG_DAYS
}

/** What qualifies a score taken from the latest confidence day: see scoreCaveat. */
export type ScoreCaveat =
  | { kind: 'stale'; logDate: string }
  | { kind: 'coverage' }
  | { kind: 'partial'; names: number; held: number }

/**
 * The ONE caveat a score shown from the latest log day carries, most important first:
 * the log is stale (the scores are old: say which day); the holdings are unknown (the
 * scorecard could not be read, so nothing says whether the scored names are all of
 * them); or fewer names were scored than are held (the highest of 3 is not the highest
 * of 24). Stale wins because it holds whatever the coverage; coverage and partial
 * cannot both apply. Null when the scores can be taken at face value, and when there
 * is no score to qualify (an empty log, or a latest day with no usable score). The
 * exit-valve tile and the overlay's θ row both apply this, so they carry the same
 * caveat in the same precedence.
 */
export function scoreCaveat(v: ValveSummary, today: string): ScoreCaveat | null {
  if (v.top === null) return null
  if (v.logDate !== null && isStaleLog(v.logDate, today)) return { kind: 'stale', logDate: v.logDate }
  if (v.held === null) return { kind: 'coverage' }
  if (v.names < v.held) return { kind: 'partial', names: v.names, held: v.held }
  return null
}

// ----------------------------------------------------------------- taxFace

export interface TaxFace {
  available: boolean
  reportable: number | null
  economic: number | null
  deferredNet: number | null
  washCount: number
  updatedAt: string | null
}

/**
 * The TaxSummaryCard arithmetic. Economic is the server's own figure
 * (Σ proceeds − unadjusted basis); only a pre-042 API without it falls back to
 * reportable − disallowed. Deferred is what is still parked in basis: disallowed
 * − recovered, which can be negative. Missing figures count as 0, as the card
 * does; a summary that is not available is all nulls.
 */
export function taxFace(summary: TaxSummary | undefined): TaxFace {
  if (!summary || summary.available !== true) {
    return { available: false, reportable: null, economic: null, deferredNet: null, washCount: 0, updatedAt: null }
  }
  const reportable = num(summary.total_net_gain_loss) ?? 0
  const disallowed = num(summary.wash_sale_disallowed) ?? 0
  const recovered = num(summary.wash_sale_recovered) ?? 0
  return {
    available: true,
    reportable,
    economic: finite(num(summary.economic_net_gain_loss) ?? reportable - disallowed),
    deferredNet: finite(disallowed - recovered),
    washCount: num(summary.transactions?.wash_sales) ?? 0,
    updatedAt: nonEmptyText(summary.calculated_at),
  }
}

// --------------------------------------------------------------- heatCells

export interface HeatCells {
  cells: HeatCell[]
  /**
   * Market value of everything held, or null when any holding has no usable market
   * value: a sum over the rest would read as the whole book.
   */
  total: number | null
  /** Holdings with no usable figure for the chosen window (shown as a dash, no tone). */
  unknown: number
  /** Tint saturation point for the grid (80th-percentile magnitude, floor 1): see lib/heatScale. */
  scale: number
}

/**
 * Which per-position return the heat grid colours by. The broker snapshot carries
 * two figures a position: today's intraday move and the move since entry. DAY is
 * the day move; every longer timeframe is since entry (there is no per-position
 * 1W/1M/3M return in the positions payload), mirroring `buildPickBars`.
 */
export type HeatWindow = 'day' | 'entry'

export const heatWindow = (tf: Timeframe): HeatWindow => (tf === 'DAY' ? 'day' : 'entry')

/** Larger first; an unknown (null) value sorts last. */
const byValueDesc = (a: number | null, b: number | null): number =>
  a === b ? 0 : a === null ? 1 : b === null ? -1 : b - a

/**
 * One heat cell per holding, largest position first (ties by symbol). The cell
 * value is the position's move in percent for `window`: the day move (null when
 * the broker has no day figure — market closed, no lastday price) or the move
 * since entry. An unknown figure shows a dash with no tone, never a 0.0%.
 * `total` is the market value of everything held, or null as soon as one holding
 * has none (its cell is still listed); `unknown` counts the cells without a
 * figure for the window.
 */
export function heatCells(positions: Position[] | undefined, window: HeatWindow = 'day'): HeatCells {
  const rows = objects<Position>(positions).map((p) => ({
    symbol: typeof p.symbol === 'string' ? p.symbol : '',
    marketValue: num(p.market_value),
    move: num(window === 'day' ? p.unrealized_intraday_pnl_percent : p.unrealized_pnl_pct),
  }))
  rows.sort((a, b) => byValueDesc(a.marketValue, b.marketValue) || compareText(a.symbol, b.symbol))
  return {
    cells: rows.map((r) => ({
      label: r.symbol,
      value: r.move,
      display: r.move === null ? DASH : fmtPct(r.move, 1),
    })),
    total: rows.every((r) => r.marketValue !== null)
      ? finite(rows.reduce((sum, r) => sum + (r.marketValue ?? 0), 0))
      : null,
    unknown: rows.filter((r) => r.move === null).length,
    scale: heatScale(rows.map((r) => r.move)),
  }
}

// --------------------------------------------------------------- scoreBins

const BIN_COUNT = 10

/**
 * Ten [from, to) score bins from 0.50 to 1.00 in steps of 0.05. Each edge is a
 * division of two integers, so it is the double nearest the decimal it names:
 * `from` of the .70 bin is exactly 0.7, and a θ of 0.7 compares equal to it.
 * (Repeated addition drifts and misplaces that edge.)
 */
export const SCORE_BINS: ReadonlyArray<[number, number]> = Array.from(
  { length: BIN_COUNT },
  (_, i): [number, number] => [(500 + 50 * i) / 1000, (550 + 50 * i) / 1000],
)

export interface ScoreBins {
  bins: HistBin[]
  names: number
  logDate: string | null
}

/** The bin a score falls in: the first whose upper edge it is below (from <= score < to), 1.0 and above in the last. */
const binOf = (score: number): number => {
  const i = SCORE_BINS.findIndex(([, to]) => score < to)
  return i === -1 ? SCORE_BINS.length - 1 : i
}

/**
 * The latest log day's scores in the ten bins (the first and last bins take
 * everything below 0.5 and at or above 1.0). The score is compared RAW against the
 * edges, exactly as the valve compares it: the engine stores round(score, 6) of a
 * continuous blend and fires on `score >= θ`, so the .70 bin is the scores the
 * valve fires on at θ 0.70 and a 0.6996 that does not fire is in the .65 bin.
 * Rounding to a coarser grid would put a name just under θ in the bin that reads
 * "would fire". The edges are exact doubles, so a score of exactly 0.7 is in the
 * .70 bin.
 */
export function scoreBins(log: ConfidenceLog | undefined): ScoreBins {
  const { logDate, rows } = latestScores(log)
  const counts = SCORE_BINS.map(() => 0)
  for (const { score } of rows) counts[binOf(score)] += 1
  return {
    bins: SCORE_BINS.map(([from, to], i): HistBin => ({
      label: from.toFixed(2).slice(1),
      count: counts[i],
      from,
      to,
    })),
    names: rows.length,
    logDate,
  }
}

// ----------------------------------------------------------------- fmtScore

/**
 * A conviction score as three decimals, TRUNCATED and never rounded. The valve
 * fires on the raw `score >= θ`, so a name at 0.6996 must not print as 0.700, the
 * figure that reads "at θ". The 1e-9 absorbs float noise in a computed score (0.1 +
 * 0.57 is 0.6699999999999999). A dash for a missing or unusable score.
 */
export function fmtScore(score: number | null | undefined): string {
  if (typeof score !== 'number' || !Number.isFinite(score * 1000)) return DASH
  return (Math.floor(score * 1000 + 1e-9) / 1000).toFixed(3)
}

// -------------------------------------------------------------- benchStrip

export interface BenchStrip {
  points: StripPoint[]
  lowestHeld: number | null
  domain: [number, number]
  above: number
  benched: number
}

/** StripPlot has exactly four label slots; a fifth label would collide with one. */
const BENCH_LABELS = 4
/** The least span the axis is padded from, so a single value still gets a width. */
const BENCH_MIN_SPAN = 0.1
const BENCH_PAD = 0.1

/**
 * The benched names on a trend-excess axis against the weakest name held
 * (ruling 25: the bench promotes strongest `sue` first when a slot opens, and θ
 * plays no part). Points are the benched names with a finite trend excess,
 * strongest first; the best four carry labels. A point is `gain` (and counted in
 * `above`) when it is strictly above the lowest held name's `sue`; with no held
 * name there is no lowest held and nothing is above it. The domain spans the
 * points and the lowest held, padded by 10% of the span (at least 0.1), or [0, 1]
 * when there is nothing to draw.
 */
export function benchStrip(
  roster: ConvictionRoster | undefined,
  scorecard: ConvictionScorecard | undefined,
): BenchStrip {
  const benched = objects<RosterCandidate>(roster?.candidates)
    .flatMap((c) => {
      const value = c.state === 'benched' ? num(c.trend_excess) : null
      return value === null ? [] : [{ symbol: typeof c.symbol === 'string' ? c.symbol : '', value }]
    })
    .sort((a, b) => b.value - a.value || compareText(a.symbol, b.symbol))

  const heldSue = objects<ScorecardPosition>(scorecard?.positions)
    .filter((p) => p.state === 'held')
    .map((p) => num(p.sue))
    .filter((v): v is number => v !== null)
  const lowestHeld = heldSue.length > 0 ? heldSue.reduce((m, v) => Math.min(m, v)) : null

  const points = benched.map(({ symbol, value }, i): StripPoint => ({
    label: symbol,
    value,
    tone: lowestHeld !== null && value > lowestHeld ? 'gain' : 'neutral',
    ...(i < BENCH_LABELS ? { labelled: true } : {}),
  }))

  const values = points.map((p) => p.value)
  if (lowestHeld !== null) values.push(lowestHeld)
  let domain: [number, number] = [0, 1]
  if (values.length > 0) {
    const lo = values.reduce((m, v) => Math.min(m, v))
    const hi = values.reduce((m, v) => Math.max(m, v))
    const pad = Math.max(hi - lo, BENCH_MIN_SPAN) * BENCH_PAD
    if (Number.isFinite(lo - pad) && Number.isFinite(hi + pad)) domain = [lo - pad, hi + pad]
  }

  return {
    points,
    lowestHeld,
    domain,
    above: points.filter((p) => p.tone === 'gain').length,
    benched: points.length,
  }
}

// ------------------------------------------------------------ cadenceLanes

const LANE_LABELS = ['Discovery', 'Bench re-score', 'Entries ~', 'Stale-out', 'Sector eval'] as const
const DEFAULT_CONFIRM_N = 5
/** Far past any real confirmation period; keeps addBusinessDays' loop bounded. */
const MAX_CONFIRM_N = 366
/** The longest window the Timeline draws (its MAX_DAYS): a date past it can be neither a dot nor off the edge. */
const MAX_WINDOW_DAYS = 366

export interface CadenceLanes {
  lanes: TimelineLane[]
  next: { label: string; date: string } | null
}

/** A dated cadence event: `done` when it has happened. */
interface Dated {
  date: string
  done: boolean
}

/** An event on a calendar schedule (discovery, the weekly re-score): done once its date is behind `today`. */
const derived = (date: string | null, today: string): Dated[] =>
  date === null ? [] : [{ date, done: date < today }]

/**
 * The dates of names that are still incubating: never done. A date behind today
 * means the entry or the stale-out has NOT happened (confirmation slipped, a gate
 * blocks it), so it is overdue and stays a scheduled dot against the today line.
 */
const pending = (dates: string[]): Dated[] => dates.map((date) => ({ date, done: false }))

/**
 * The jobs of the research cadence on a window of `days` days from `start`, one
 * lane each, in fixed order: Discovery, Bench re-score, Entries ~, Stale-out,
 * Sector eval.
 *   - Discovery and Bench re-score are calendar schedules: only the UPCOMING date is
 *     drawn, as scheduled. A past one is never drawn as done: nothing proves it ran
 *     (an engine halt across a Tuesday leaves the re-score unrun).
 *   - Entries ~ (each incubating name's flag date + confirm_n business days) and
 *     Stale-out (each one's stale_out_date) are per-name state of names that are
 *     STILL incubating, so they are never done. Every distinct date is drawn, so
 *     one stuck name cannot hide the upcoming ones; a date behind today stays a
 *     scheduled dot, which reads as overdue against the today line.
 *   - Sector eval: each completed run is done (its ET day), the expected run is
 *     scheduled.
 *
 * Placement is the same for every lane: a date before `start` is dropped, a date
 * inside the window is an event, and an upcoming date at or past its end is not
 * an event but the lane's `offRight` chip, for the earliest such date. The
 * earliest scheduled (not done) event dated today or later gets the one label,
 * its short date, and is returned as `next` with its lane's name (an earlier lane
 * wins a tie).
 */
export function cadenceLanes(
  roster: ConvictionRoster | undefined,
  runs: SectorShiftRuns | undefined,
  today: string,
  start: string,
  days: number,
): CadenceLanes {
  const empty = LANE_LABELS.map((label): TimelineLane => ({ label, events: [] }))
  if (!isISODate(today) || !isISODate(start)) return { lanes: empty, next: null }

  const windowDays = Number.isFinite(days) ? Math.max(0, Math.floor(Math.min(days, MAX_WINDOW_DAYS))) : 0

  const incubating = objects<RosterCandidate>(roster?.candidates).filter((c) => c.state === 'incubating')
  const confirmValue = num(roster?.confirm_n)
  const confirmN =
    confirmValue !== null && confirmValue >= 0 ? Math.min(Math.floor(confirmValue), MAX_CONFIRM_N) : DEFAULT_CONFIRM_N

  const nextTuesday = nextTuesdayISO(today)
  const entryDates = incubating.flatMap((c) => {
    const flagged = dayOf(c.flag_date)
    return flagged === null ? [] : [addBusinessDays(flagged, confirmN)]
  })
  const staleDates = incubating.flatMap((c) => {
    const stale = dayOf(c.stale_out_date)
    return stale === null ? [] : [stale]
  })
  const completed = objects<SectorShiftRunDay>(runs?.days).flatMap((d) => {
    const day = etDayOf(d.completed_at)
    return day === null ? [] : [{ date: day, done: true }]
  })
  const expected = etDayOf(runs?.schedule?.expected_run_at)

  const items: Dated[][] = [
    derived(nextDiscoveryISO(today), today),
    derived(nextTuesday, today), // upcoming only: nothing proves the previous re-score ran
    pending(entryDates),
    pending(staleDates),
    [...completed, ...(expected === null ? [] : [{ date: expected, done: false }])],
  ]

  const lanes = LANE_LABELS.map((label, i): TimelineLane => {
    // One entry per date, a completed one winning over a scheduled one.
    const byDate = new Map<string, boolean>()
    for (const { date, done } of items[i]) byDate.set(date, (byDate.get(date) ?? false) || done)

    const events: TimelineEvent[] = []
    let offRight: string | null = null
    for (const date of [...byDate.keys()].sort()) {
      const done = byDate.get(date) === true
      const offset = daysUntil(date, start)
      if (!Number.isFinite(offset) || offset < 0) continue // before the window: not drawn
      if (offset >= windowDays) {
        if (!done && offRight === null) offRight = date // upcoming, past the right edge: the chip
        continue
      }
      events.push(done ? { date, done: true } : { date })
    }
    return offRight === null ? { label, events } : { label, events, offRight: shortDate(offRight) }
  })

  // The earliest scheduled event from today on; strict < keeps the earlier lane on a tie.
  let next: { lane: number; event: number; date: string } | null = null
  for (let l = 0; l < lanes.length; l += 1) {
    for (let e = 0; e < lanes[l].events.length; e += 1) {
      const { date, done } = lanes[l].events[e]
      if (done || date < today) continue
      if (next === null || date < next.date) next = { lane: l, event: e, date }
    }
  }
  if (next === null) return { lanes, next: null }

  const lane = lanes[next.lane]
  lane.events[next.event] = { ...lane.events[next.event], label: shortDate(next.date) }
  return { lanes, next: { label: lane.label, date: next.date } }
}

// ----------------------------------------------------------- overlayBullets

export interface OverlayBullets {
  rows: BulletRow[]
  flags: BulletFlag[]
}

/** The drawdown floor's scale: a floor above 50% is off the end of the bar. */
const FLOOR_SCALE_MAX = 0.5

const percentText = (fraction: number | null): string =>
  fraction === null ? DASH : `${(fraction * 100).toFixed(1)}%`

/**
 * The overlay face: three knobs, each as its setting against its live value,
 * and two flags. θ: the status θ against the HIGHEST latest score (ruling 24),
 * the score printed truncated (fmtScore) so a name under θ never reads as at θ.
 * cap: the 25-slot limit against the positions held. floor: the drawdown floor
 * against the live drawdown (the last known one while the market is closed), both
 * unsigned percents; a disarmed floor (`enabled: false`) is labelled "floor off"
 * so it does not read as armed. An unknown setting or live value is null with a
 * dash. Flags:
 * `entries` is the EFFECTIVE flag: the status's `entries_enabled` is the settings kill
 * switch AND the overlay (src/api/routes/conviction.py), so it is off when the status
 * says false OR the overlay says `conviction_entries_enabled: false` (the overlay can
 * be newer than the status, and the settings can kill entries with an empty overlay),
 * on only when neither is false and at least one is a known boolean, and unknown when
 * neither is. A status without a boolean flag falls back to the overlay alone; an
 * overlay record that does not say false is known (the backend defaults it to true).
 * `scaling` is the drawdown-floor exposure flag.
 */
export function overlayBullets(
  overlay: OverlayView | undefined,
  status: ConvictionStatus | undefined,
  valve: ValveSummary,
  positions: Position[] | undefined,
  dd: DrawdownFloorStatus | undefined,
): OverlayBullets {
  const theta = thetaOf(status)
  const score = num(valve?.top?.score)
  const held = Array.isArray(positions) ? objects<Position>(positions).length : null
  const floor = num(dd?.floor)
  const drawdown = num(dd?.drawdown) ?? num(dd?.last_known_drawdown)
  const scaling = dd?.exposure?.enabled
  const statusEntries = typeof status?.entries_enabled === 'boolean' ? status.entries_enabled : null
  const overlayEntries = isRecord(overlay)
    ? !(isRecord(overlay.overlay) && overlay.overlay.conviction_entries_enabled === false)
    : null

  return {
    rows: [
      {
        label: 'θ',
        min: THETA_MIN,
        max: THETA_MAX,
        setting: theta,
        live: score,
        settingDisplay: theta === null ? DASH : theta.toFixed(2),
        liveDisplay: fmtScore(score),
      },
      {
        label: 'cap',
        min: 0,
        max: POSITION_CAP,
        setting: POSITION_CAP,
        live: held,
        settingDisplay: String(POSITION_CAP),
        liveDisplay: held === null ? DASH : String(held),
      },
      {
        label: dd?.enabled === false ? 'floor off' : 'floor',
        min: 0,
        max: FLOOR_SCALE_MAX,
        setting: floor,
        live: drawdown,
        settingDisplay: percentText(floor),
        liveDisplay: percentText(drawdown),
      },
    ],
    flags: [
      { label: 'entries', on: statusEntries === false || overlayEntries === false ? false : (statusEntries ?? overlayEntries) },
      { label: 'scaling', on: typeof scaling === 'boolean' ? scaling : null },
    ],
  }
}
